import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, API, fileUrl, formatFollowers } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Instagram, Music2, Youtube, BadgeCheck, Loader2, Link2, Unlink, RefreshCw, ShieldCheck, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";


const FAIL_HINTS = {
  state_not_returned: "The connection returned without a security token and no matching session was found. Please try connecting again from this device.",
  stored_state_missing: "Your connection session expired before it finished. Please try connecting again.",
  state_expired: "Your connection session expired before it finished. Please try connecting again.",
  state_mismatch: "The security token didn't match. For your safety the connection was stopped — please try again.",
  start_expired: "The connection link expired. Please tap Connect again.",
  missing_code: "Instagram returned to Nearfluence without an authorization code. Please try connecting again.",
  unexpected_post: "Instagram's response came back in an unexpected format. Please try connecting again.",
  token_exchange: "We couldn't complete the secure handshake with Instagram. Please try again in a moment.",
  profile_import: "We connected but couldn't import your profile details. Please try again.",
};

const META = {
  instagram: { label: "Instagram", Icon: Instagram, color: "text-pink-600", ring: "ring-pink-100" },
  tiktok: { label: "TikTok", Icon: Music2, color: "text-zinc-900", ring: "ring-zinc-100" },
  youtube: { label: "YouTube", Icon: Youtube, color: "text-red-600", ring: "ring-red-100" },
};

function Stat({ label, value }) {
  if (value === null || value === undefined) return null;
  return <div className="text-center"><p className="font-display text-lg font-bold text-zinc-900">{typeof value === "number" ? formatFollowers(value) : value}</p><p className="text-[11px] text-zinc-500">{label}</p></div>;
}

export function ConnectedSocials({ compact = false, onUsePhoto }) {
  const { user } = useAuth();
  const role = user?.role;
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [photoBusy, setPhotoBusy] = useState("");
  const [refreshing, setRefreshing] = useState("");
  const [disc, setDisc] = useState(null);
  const [params, setParams] = useSearchParams();

  const load = () => api.get("/social/providers").then((r) => setProviders(r.data.providers)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const s = params.get("social_connected");
    if (s === "1") {
      toast.success(`${(params.get("platform") || "Account").replace(/^\w/, (c) => c.toUpperCase())} connected & verified ✓`);
      load();
    } else if (s === "0") {
      const platform = (params.get("platform") || "account").replace(/^\w/, (c) => c.toUpperCase());
      const fail = params.get("fail") || "";
      const providerMsg = params.get("err_desc") || params.get("err_reason") || "";
      const msg = providerMsg || FAIL_HINTS[fail] || `We couldn't finish connecting your ${platform}. Please try again.`;
      toast.error(msg, { duration: 9000 });
    }
    if (s) {
      ["social_connected", "platform", "fail", "err", "err_reason", "err_desc"].forEach((k) => params.delete(k));
      setParams(params, { replace: true });
    }
  }, [params, setParams]);

  const connect = async (platform) => {
    setBusy(platform);
    try {
      const r = await api.get(`/social/connect/${platform}`);
      // Navigate via our own /start bounce endpoint (top-level navigation) so a first-party
      // httpOnly state cookie is set on our domain before we hand off to the provider.
      if (r.data.start_token) {
        window.location.href = `${API}/social/start/${platform}?st=${encodeURIComponent(r.data.start_token)}`;
      } else {
        window.location.href = r.data.authorize_url;
      }
    } catch (e) {
      toast.info(e.response?.data?.detail || "This connection isn't available yet.");
      setBusy("");
    }
  };

  const disconnect = async () => {
    const platform = disc;
    setDisc(null); setBusy(platform);
    try { await api.post(`/social/disconnect/${platform}`); toast.success("Disconnected"); load(); }
    catch { toast.error("Could not disconnect"); }
    finally { setBusy(""); }
  };

  const applyPhoto = async (platform) => {
    setPhotoBusy(platform);
    try {
      const r = await api.post(`/social/${platform}/use-photo`);
      toast.success(role === "business" ? "Instagram photo set as your business image" : "Instagram photo set as your profile photo");
      onUsePhoto?.(r.data.url);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't use that photo. Your current photo was kept.");
    } finally { setPhotoBusy(""); }
  };

  const refresh = async (platform) => {
    setRefreshing(platform);
    try { await api.post(`/social/${platform}/refresh`); toast.success("Stats refreshed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't refresh right now."); }
    finally { setRefreshing(""); }
  };

  if (loading) return <div className="flex h-24 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-emerald-600" /></div>;

  return (
    <div data-testid="connected-socials" className="rounded-3xl border border-zinc-200 bg-white p-6">
      <div className="flex items-start gap-2">
        <ShieldCheck className="mt-0.5 h-5 w-5 text-emerald-600" />
        <div>
          <h2 className="font-display text-lg font-bold text-zinc-900">Connected socials</h2>
          <p className="text-sm text-zinc-500">{compact
            ? "Connect your business accounts through the official apps to earn a verified badge. Creators trust businesses with verified socials."
            : "Connect your accounts through the official apps to get a verified badge and auto-import your real stats. Businesses trust verified profiles more."}</p>
        </div>
      </div>

      <div className="mt-5 space-y-3">
        {providers.map((p) => {
          const m = META[p.platform];
          const c = p.connection;
          return (
            <div key={p.platform} data-testid={`social-provider-${p.platform}`} className={`rounded-2xl border p-4 ${c ? "border-emerald-200 bg-emerald-50/40" : "border-zinc-200"}`}>
              <div className="flex items-center gap-3">
                <div className={`flex h-11 w-11 items-center justify-center rounded-xl bg-white ring-4 ${m.ring}`}><m.Icon className={`h-6 w-6 ${m.color}`} /></div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-display font-semibold text-zinc-900">{m.label}</p>
                    {c ? (
                      <span data-testid={`verified-${p.platform}`} className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700"><BadgeCheck className="h-3.5 w-3.5" /> Verified</span>
                    ) : !p.available ? (
                      <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-500">Coming soon</span>
                    ) : !p.configured ? (
                      <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">Enabling soon</span>
                    ) : null}
                  </div>
                  {c ? <div className="flex items-center gap-1.5">
                      {c.profile_image && <img src={c.profile_image} alt="" referrerPolicy="no-referrer" className="h-5 w-5 rounded-full object-cover" />}
                      <p className="truncate text-sm text-zinc-500">@{c.username}{c.last_success_sync ? ` · updated ${new Date(c.last_success_sync).toLocaleDateString()}` : ""}</p>
                    </div>
                    : <p className="text-sm text-zinc-400">Not connected</p>}
                </div>
                {c ? (
                  <div className="flex items-center gap-1.5">
                    <Button size="sm" variant="outline" data-testid={`refresh-${p.platform}`} disabled={refreshing === p.platform} onClick={() => refresh(p.platform)} className="rounded-full border-zinc-200 px-2.5 text-zinc-600 hover:bg-zinc-50" title="Refresh stats">
                      {refreshing === p.platform ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                    </Button>
                    <Button size="sm" variant="outline" data-testid={`disconnect-${p.platform}`} disabled={busy === p.platform} onClick={() => setDisc(p.platform)} className="rounded-full border-rose-200 text-rose-600 hover:bg-rose-50"><Unlink className="mr-1 h-3.5 w-3.5" /> Disconnect</Button>
                  </div>
                ) : p.available ? (
                  <Button size="sm" data-testid={`connect-${p.platform}`} disabled={busy === p.platform} onClick={() => connect(p.platform)} className="rounded-full bg-emerald-600 hover:bg-emerald-700">
                    {busy === p.platform ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Link2 className="mr-1 h-3.5 w-3.5" /> Connect</>}
                  </Button>
                ) : (
                  <Button size="sm" variant="outline" disabled data-testid={`connect-${p.platform}`} className="rounded-full border-zinc-200 text-zinc-400">Coming soon</Button>
                )}
              </div>

              {c && !compact && (
                <>
                  <div className="mt-4 flex items-center gap-3 border-t border-emerald-100 pt-3">
                    {c.profile_image && <img src={c.profile_image} alt="" referrerPolicy="no-referrer" className="h-10 w-10 rounded-full object-cover" />}
                    <div className="grid flex-1 grid-cols-4 gap-1">
                      <Stat label="Followers" value={c.followers} />
                      <Stat label="Following" value={c.following} />
                      <Stat label={p.platform === "tiktok" ? "Videos" : "Posts"} value={c.media_count} />
                      {c.total_likes != null ? <Stat label="Likes" value={c.total_likes} />
                        : c.metrics?.engagement_rate != null ? <Stat label="Engage" value={`${c.metrics.engagement_rate}%`} /> : <div />}
                    </div>
                  </div>
                  {c.recent_media?.length > 0 && (
                    <div className="mt-3 flex gap-2 overflow-x-auto no-scrollbar">
                      {c.recent_media.slice(0, 6).map((r) => (
                        <a key={r.id} href={r.permalink} target="_blank" rel="noreferrer" className="h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-zinc-100">
                          {r.thumbnail && <img src={r.thumbnail} alt="" referrerPolicy="no-referrer" className="h-full w-full object-cover" />}
                        </a>
                      ))}
                    </div>
                  )}
                  {c.profile_image && (
                    <div className="mt-3 flex items-center gap-3 border-t border-emerald-100 pt-3">
                      <img src={c.profile_image} alt="" referrerPolicy="no-referrer" className="h-9 w-9 rounded-full object-cover ring-2 ring-white" />
                      <Button size="sm" variant="outline" data-testid={`use-photo-${p.platform}`} disabled={photoBusy === p.platform}
                        onClick={() => applyPhoto(p.platform)} className="rounded-full border-emerald-200 text-emerald-700 hover:bg-emerald-50">
                        {photoBusy === p.platform ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <ImageIcon className="mr-1 h-3.5 w-3.5" />}
                        {role === "business" ? "Use as business photo" : "Use as profile photo"}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      <Dialog open={!!disc} onOpenChange={(o) => !o && setDisc(null)}>
        <DialogContent data-testid="disconnect-dialog">
          <DialogHeader>
            <DialogTitle>Disconnect {disc && META[disc]?.label}?</DialogTitle>
            <DialogDescription>This removes the verified badge and deletes the imported social data from Nearfluence. You can reconnect at any time.</DialogDescription>
          </DialogHeader>
          <Button data-testid="confirm-disconnect" onClick={disconnect} className="w-full rounded-full bg-rose-600 hover:bg-rose-700"><Unlink className="mr-2 h-4 w-4" /> Disconnect</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
